import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AdminBasemap, LayerGroup, MapSettings } from './types';
import { seedBasemaps } from './store';
import { basemapsApi } from '../lib/basemapsApi';
import { layerGroupsApi } from '../lib/layerGroupsApi';
import { mapsApi } from '../lib/mapsApi';

type AdminDataContextValue = {
  basemaps: AdminBasemap[];
  basemapsLoading: boolean;
  createBasemap: (basemap: Omit<AdminBasemap, 'id'>) => Promise<AdminBasemap>;
  updateBasemap: (id: string, basemap: Omit<AdminBasemap, 'id'>) => Promise<AdminBasemap>;
  deleteBasemap: (id: string) => Promise<void>;
  layerGroups: LayerGroup[];
  addLayerGroup: (group: Omit<LayerGroup, 'id' | 'sortOrder'>) => Promise<void>;
  updateLayerGroup: (id: string, patch: Partial<Omit<LayerGroup, 'id'>>) => Promise<void>;
  deleteLayerGroup: (id: string) => Promise<void>;
  moveLayerGroup: (id: string, direction: 'up' | 'down') => Promise<void>;
  mapSettings: MapSettings | null;
  mapSettingsLoading: boolean;
  mapSettingsError: string | null;
  updateMapSettings: (settings: MapSettings) => Promise<MapSettings>;
  createMap: (settings: MapSettings) => Promise<MapSettings>;
};

const AdminDataContext = createContext<AdminDataContextValue | null>(null);

export function useAdminData(): AdminDataContextValue {
  const ctx = useContext(AdminDataContext);
  if (!ctx) throw new Error('useAdminData must be used within <AdminDataProvider>');
  return ctx;
}

export function AdminDataProvider({ children }: { children: ReactNode }) {
  const [basemaps, setBasemaps] = useState<AdminBasemap[]>([]);
  const [basemapsLoading, setBasemapsLoading] = useState(true);
  const [layerGroups, setLayerGroups] = useState<LayerGroup[]>([]);
  const [mapSettings, setMapSettings] = useState<MapSettings | null>(null);
  const [mapSettingsLoading, setMapSettingsLoading] = useState(true);
  const [mapSettingsError, setMapSettingsError] = useState<string | null>(null);

  // Groups live in gis.layer_groups now (shared across every user of the
  // workspace, visible to every role — see maps-service's layer_groups
  // router), not in localStorage, so this waits for mapSettings.id.
  useEffect(() => {
    if (!mapSettings?.id) {
      setLayerGroups([]);
      return;
    }
    let cancelled = false;
    layerGroupsApi
      .listLayerGroups(mapSettings.id)
      .then((rows) => {
        if (!cancelled) setLayerGroups(rows);
      })
      .catch(() => {
        if (!cancelled) setLayerGroups([]);
      });
    return () => {
      cancelled = true;
    };
  }, [mapSettings?.id]);

  useEffect(() => {
    let cancelled = false;
    basemapsApi
      .listBasemaps()
      .then(async (rows) => {
        if (rows.length > 0) {
          if (!cancelled) setBasemaps(rows);
          return;
        }
        // Свежее пространство без подложек — засеваем тремя бесплатными
        // публичными источниками, чтобы карта не осталась без фона.
        const seeded = await Promise.all(seedBasemaps.map((b) => basemapsApi.createBasemap(b)));
        if (!cancelled) setBasemaps(seeded);
      })
      .catch(() => {
        if (!cancelled) setBasemaps([]);
      })
      .finally(() => {
        if (!cancelled) setBasemapsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    mapsApi
      .listMaps()
      .then((maps) => {
        if (!cancelled) setMapSettings(maps[0] ?? null);
      })
      .catch((err) => {
        if (!cancelled) setMapSettingsError(err instanceof Error ? err.message : 'Не удалось загрузить карту.');
      })
      .finally(() => {
        if (!cancelled) setMapSettingsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const createBasemap = async (basemap: Omit<AdminBasemap, 'id'>) => {
    const saved = await basemapsApi.createBasemap({ ...basemap, id: '' });
    setBasemaps((prev) => (saved.isDefault ? prev.map((b) => ({ ...b, isDefault: false })) : prev).concat(saved));
    return saved;
  };

  const updateBasemap = async (id: string, basemap: Omit<AdminBasemap, 'id'>) => {
    const saved = await basemapsApi.updateBasemap(id, { ...basemap, id });
    setBasemaps((prev) =>
      prev.map((b) => {
        if (b.id === saved.id) return saved;
        return saved.isDefault ? { ...b, isDefault: false } : b;
      }),
    );
    return saved;
  };

  const deleteBasemap = async (id: string) => {
    await basemapsApi.deleteBasemap(id);
    setBasemaps((prev) => prev.filter((b) => b.id !== id));
  };

  const addLayerGroup = async (group: Omit<LayerGroup, 'id' | 'sortOrder'>) => {
    if (!mapSettings?.id) throw new Error('карта ещё не создана — сначала сохраните её настройки');
    const saved = await layerGroupsApi.createLayerGroup(mapSettings.id, group);
    setLayerGroups((prev) => [...prev, saved]);
  };

  const updateLayerGroup = async (id: string, patch: Partial<Omit<LayerGroup, 'id'>>) => {
    const current = layerGroups.find((g) => g.id === id);
    if (!current) return;
    const saved = await layerGroupsApi.updateLayerGroup(id, { ...current, ...patch });
    setLayerGroups((prev) => prev.map((g) => (g.id === id ? saved : g)));
  };

  const deleteLayerGroup = async (id: string) => {
    await layerGroupsApi.deleteLayerGroup(id);
    setLayerGroups((prev) => {
      // gis.layer_groups cascades child-group deletes in the DB, but the
      // response to DELETE carries no body — drop descendants locally too.
      const toRemove = new Set<string>([id]);
      let grew = true;
      while (grew) {
        grew = false;
        for (const g of prev) {
          if (g.parentId && toRemove.has(g.parentId) && !toRemove.has(g.id)) {
            toRemove.add(g.id);
            grew = true;
          }
        }
      }
      return prev.filter((g) => !toRemove.has(g.id));
    });
  };

  const moveLayerGroup = async (id: string, direction: 'up' | 'down') => {
    const updated = await layerGroupsApi.moveLayerGroup(id, direction);
    if (updated.length === 0) return;
    setLayerGroups((prev) => prev.map((g) => updated.find((u) => u.id === g.id) ?? g));
  };

  const updateMapSettings = async (settings: MapSettings) => {
    if (!settings.id) throw new Error('map has no id yet — call createMap first');
    const saved = await mapsApi.updateMap(settings.id, settings);
    setMapSettings(saved);
    return saved;
  };

  const createMap = async (settings: MapSettings) => {
    const saved = await mapsApi.createMap(settings);
    setMapSettings(saved);
    return saved;
  };

  const value = useMemo<AdminDataContextValue>(
    () => ({
      basemaps,
      basemapsLoading,
      createBasemap,
      updateBasemap,
      deleteBasemap,
      layerGroups,
      addLayerGroup,
      updateLayerGroup,
      deleteLayerGroup,
      moveLayerGroup,
      mapSettings,
      mapSettingsLoading,
      mapSettingsError,
      updateMapSettings,
      createMap,
    }),
    [basemaps, basemapsLoading, layerGroups, mapSettings, mapSettingsLoading, mapSettingsError],
  );

  return <AdminDataContext.Provider value={value}>{children}</AdminDataContext.Provider>;
}
